import { WhatsAppOutlined } from '@ant-design/icons';
import { Button, Collapse, Descriptions, Space, Spin, Tag, Typography } from 'antd';
import parsePhoneNumber from 'libphonenumber-js';
import moment from 'moment';
import React from 'react';
import NumberFormat from 'react-number-format';
import { useIsFetching } from 'react-query';

import useAPIQuery from '../api/useAPIQuery';
import ErrorResult from '../components/ErrorResult';
import MultilineParagraph from '../components/MultilineParagraph';
import useTypedParams, { Optional } from '../hooks/useTypedParams';
import { withRouteChange } from '../services/APM';
import { AICandidateResumeSummary } from '../sinapse/generate_resume_summary';
import { CandidateId, JobPostingId } from '../types';
import styles from './ApplicantDetails.module.css';

const RecentRecruitmentProcessJobPostingTitle = ({ stageId }: { stageId: number }) => {
  const jobPostings = useAPIQuery([
    '/job-postings/',
    {
      params: {
        stage: stageId,
      },
    },
  ]);

  return (
    <>
      {jobPostings.data?.results[0]?.name || ''}
      <br />
    </>
  );
};

const ApplicantDetails = () => {
  const { id, applicant_id } = useTypedParams({
    id: Optional(JobPostingId),
    applicant_id: CandidateId,
  });

  const candidate = useAPIQuery(`/candidates/${applicant_id}/`);
  const config = useAPIQuery('/configs/tenant-configurations/');

  const recruitmentProcess = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: applicant_id,
          job_posting: id,
        },
      },
    ],
    {
      enabled: !!id,
      select(data) {
        return data.count > 0 ? data.results[0] : null;
      },
    }
  );

  const recentRecruitmentProcesses = useAPIQuery([
    '/recruitment-processes/',
    {
      params: {
        candidate: applicant_id,
        page_size: 4,
        ordering: '-created_at',
      },
    },
  ]);

  const candidateDisabilities = useAPIQuery([
    '/candidate-disability/',
    {
      params: {
        candidate: applicant_id,
      },
    },
  ]);

  const applicantLinkedin = candidate.data?.social_networks?.find(
    (socialNetwork) => socialNetwork.type === 'LINKEDIN'
  )?.profile;

  const calculateAge = (dateOfBirth: string | undefined) => {
    if (dateOfBirth === undefined) {
      return 0;
    }
    const birthDate = new Date(dateOfBirth);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();

    if (
      today.getMonth() < birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const isJobPostingFetching = useIsFetching(['/job-postings/']);

  const candidatePhoneNumberTypeDict = {
    PERSONAL: 'Pessoal',
    RESIDENTIAL: 'Residencial',
    PROFESSIONAL: 'Profissional',
    MOBILE: 'Móvel',
    BUSINESS_FAX: 'Fax profissional',
    PERSONAL_FAX: 'Fax pessoal',
    OTHER: 'Outro',
  };

  return (
    <ErrorResult
      error={candidate.error || recruitmentProcess.error || recentRecruitmentProcesses.error}
    >
      <Spin
        spinning={
          candidate.isLoading ||
          recruitmentProcess.isLoading ||
          recentRecruitmentProcesses.isLoading ||
          isJobPostingFetching > 0
        }
      >
        {['CANDIDATE_DETAILS', 'BOTH_TABS'].includes(
          config.data?.candidateResumeSummaryPlacement || ''
        ) ? (
          <AICandidateResumeSummary candidateId={applicant_id} />
        ) : null}
        <Descriptions
          layout="vertical"
          size="small"
          colon={false}
          labelStyle={{ fontWeight: 'bold' }}
        >
          <Descriptions.Item label="E-mail">{candidate.data?.email || '-'}</Descriptions.Item>
          <Descriptions.Item label="Linkedin">
            {applicantLinkedin ? (
              <Typography.Link href={applicantLinkedin} target="_blank">
                {applicantLinkedin}
              </Typography.Link>
            ) : (
              <Typography.Text>-</Typography.Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={id ? 'Data de inscrição na vaga' : 'Data de criação do perfil'}>
            {id
              ? moment(recruitmentProcess.data?.created_at).format('L') || '-'
              : moment(candidate.data?.created_at).format('L') || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Pretensão salarial">
            {candidate.data?.salary_expectation ? (
              <Typography.Text>
                <NumberFormat
                  prefix="R$ "
                  value={candidate.data?.salary_expectation}
                  decimalSeparator=","
                  thousandSeparator="."
                  readOnly
                  displayType="text"
                />
              </Typography.Text>
            ) : (
              <Typography.Text>-</Typography.Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Portfolio / Github / Site">
            {candidate.data?.portfolio ? (
              <Typography.Link href={candidate.data?.portfolio} target="_blank">
                {candidate.data?.portfolio}
              </Typography.Link>
            ) : (
              <Typography.Text>-</Typography.Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Data da última atualização">
            {moment(candidate.data?.updated_at).format('L') || '-'}
          </Descriptions.Item>
        </Descriptions>
        <Collapse
          ghost
          className={styles.collapse}
          expandIconPosition="end"
          defaultActiveKey={[
            'phoneContacts',
            'origin',
            'professionalSummary',
            'professionalExperiences',
            'academicEducation',
            'languages',
            'knowledge',
            'abilities',
            'personalInterests',
            'address',
            'details',
            'tags',
          ]}
        >
          {candidate.data?.tags?.length && (
            <Collapse.Panel header={<Typography.Title level={3}>Tags</Typography.Title>} key="tags">
              {candidate.data?.tags?.map((tag) => (
                <Tag key={tag.id}>{tag.name}</Tag>
              ))}
            </Collapse.Panel>
          )}
          {candidate.data?.phone_contacts?.length && (
            <Collapse.Panel
              header={<Typography.Title level={3}>Contatos</Typography.Title>}
              key="phoneContacts"
            >
              <Descriptions
                layout="vertical"
                size="small"
                colon={false}
                labelStyle={{ fontWeight: 'bold' }}
              >
                {candidate.data?.phone_contacts.map((candidatePhoneContact) => (
                  <Descriptions.Item label="Telefone" key={candidatePhoneContact.id}>
                    <Space direction="vertical">
                      <Typography.Text>
                        {candidatePhoneNumberTypeDict[candidatePhoneContact.type]}
                      </Typography.Text>
                      <Space direction="horizontal" size={0} style={{ height: 33 }}>
                        {parsePhoneNumber(
                          `+${candidatePhoneContact.country_code}${candidatePhoneContact.number}`,
                          { extract: false }
                        )?.formatInternational() ??
                          `+${candidatePhoneContact.country_code} ${candidatePhoneContact.number}`}
                        {candidatePhoneContact.type === 'PERSONAL' && (
                          <Button type="text" shape="circle">
                            <a
                              href={`https://api.whatsapp.com/send?phone=${candidatePhoneContact.country_code}${candidatePhoneContact.number}`}
                              target="_blank"
                            >
                              <WhatsAppOutlined />
                            </a>
                          </Button>
                        )}
                      </Space>
                    </Space>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </Collapse.Panel>
          )}
          {(recentRecruitmentProcesses.data?.results.length ||
            recruitmentProcess.data?.candidate_source_option) && (
            <Collapse.Panel
              header={<Typography.Title level={3}>Origem do candidato</Typography.Title>}
              key="origin"
            >
              <Descriptions
                layout="vertical"
                size="small"
                colon={false}
                labelStyle={{ fontWeight: 'bold' }}
              >
                {id && recruitmentProcess.data?.candidate_source_option && (
                  <Descriptions.Item span={3} label="Como ficou sabendo da vaga?">
                    <Typography.Text>
                      {recruitmentProcess.data.candidate_source_option}
                    </Typography.Text>
                  </Descriptions.Item>
                )}
                {recentRecruitmentProcesses.data?.results &&
                  recentRecruitmentProcesses.data.results.length > 0 && (
                    <>
                      <Descriptions.Item label="Vaga:">
                        {recentRecruitmentProcesses.data.results.map((recentRecruitmentProcess) => (
                          <RecentRecruitmentProcessJobPostingTitle
                            key={recentRecruitmentProcess.id}
                            stageId={recentRecruitmentProcess.stage.id}
                          />
                        ))}
                      </Descriptions.Item>
                      <Descriptions.Item span={2} label="Origem do candidato nas vagas">
                        {recentRecruitmentProcesses.data.results.map((recentRecruitmentProcess) => {
                          return (
                            <React.Fragment key={recentRecruitmentProcess.id}>
                              {recentRecruitmentProcess.candidate_source_option || '-'}
                              <br />
                            </React.Fragment>
                          );
                        })}
                      </Descriptions.Item>
                    </>
                  )}
                {id &&
                  recruitmentProcess.data?.candidate_source_option === 'Indicação' &&
                  recruitmentProcess.data?.indication_email && (
                    <Descriptions.Item span={3} label="Indicações">
                      <Typography.Paragraph
                        ellipsis={{
                          rows: 3,
                          expandable: true,
                          symbol: 'Ver mais',
                        }}
                      >
                        <Typography.Text>
                          {recruitmentProcess.data?.indication_email}
                          <br />
                        </Typography.Text>
                      </Typography.Paragraph>
                    </Descriptions.Item>
                  )}
              </Descriptions>
            </Collapse.Panel>
          )}
          {(candidate.data?.professional_goal || candidate.data?.professional_summary) && (
            <Collapse.Panel
              header={<Typography.Title level={3}>Resumo Profissional</Typography.Title>}
              key="professionalSummary"
            >
              <Descriptions
                layout="vertical"
                size="small"
                colon={false}
                labelStyle={{ fontWeight: 'bold' }}
              >
                <Descriptions.Item span={3} label="Objetivo profissional">
                  <MultilineParagraph>{candidate.data?.professional_goal}</MultilineParagraph>
                </Descriptions.Item>
                <Descriptions.Item span={3} label="Resumo profissional">
                  <div>
                    <MultilineParagraph>{candidate.data?.professional_summary}</MultilineParagraph>
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </Collapse.Panel>
          )}
          {candidate.data?.professional_experiences?.length && (
            <Collapse.Panel
              header={<Typography.Title level={3}>Experiências Profissionais</Typography.Title>}
              key="professionalExperiences"
            >
              <Descriptions
                layout="vertical"
                size="small"
                colon={false}
                labelStyle={{ fontWeight: 'bold' }}
              >
                {candidate.data?.professional_experiences?.map((professionalExperience) => {
                  let experiencePeriod = 'Período de experiência não informado';
                  if (professionalExperience.start_date) {
                    const professionalExperienceStart = moment(professionalExperience.start_date);
                    const professionalExperienceEnd = moment(professionalExperience.end_date);
                    const professionalExperienceDiff = professionalExperienceStart.to(
                      professionalExperienceEnd,
                      true
                    );

                    const formattedStartDate = `${professionalExperienceStart.format(
                      'MMM'
                    )} de ${professionalExperienceStart.format('YYYY')}`;
                    const formattedEndDate = professionalExperience.end_date
                      ? `${professionalExperienceEnd.format(
                          'MMM'
                        )} de ${professionalExperienceEnd.format('YYYY')}`
                      : 'até o momento';

                    experiencePeriod = professionalExperience.end_date
                      ? `${formattedStartDate} - ${formattedEndDate} (${professionalExperienceDiff})`
                      : `${formattedStartDate} - ${formattedEndDate}`;
                  }

                  return (
                    <Descriptions.Item
                      key={professionalExperience.id}
                      label={professionalExperience.job_position_name}
                      span={3}
                    >
                      <div>
                        <Typography.Paragraph>
                          {professionalExperience.company_name}
                          <br />
                          <Typography.Text type="secondary">{experiencePeriod}</Typography.Text>
                        </Typography.Paragraph>
                        {professionalExperience.description
                          ?.split('\n')
                          .map((description, index) => (
                            <Typography.Paragraph key={index}>{description}</Typography.Paragraph>
                          ))}
                      </div>
                    </Descriptions.Item>
                  );
                })}
              </Descriptions>
            </Collapse.Panel>
          )}
          {candidate?.data?.education?.length && (
            <Collapse.Panel
              header={<Typography.Title level={3}>Formações Acadêmicas</Typography.Title>}
              key="academicEducation"
            >
              <Descriptions
                layout="vertical"
                size="small"
                colon={false}
                labelStyle={{ fontWeight: 'bold' }}
              >
                {candidate.data?.education?.map((education) => (
                  <Descriptions.Item key={education.id} label={education.course_name} span={3}>
                    <div>
                      <Typography.Text>{education.institution_name}</Typography.Text> -{' '}
                      {education.type_education_level === 'ELEMENTARY_SCHOOL_REGULAR' && (
                        <Typography.Text>Ensino fundamental</Typography.Text>
                      )}
                      {education.type_education_level === 'HIGH_SCHOOL_REGULAR' && (
                        <Typography.Text>Ensino médio</Typography.Text>
                      )}
                      {education.type_education_level === 'HIGHER_EDUCATION' && (
                        <Typography.Text>Superior</Typography.Text>
                      )}
                      {education.type_education_level === 'POST_GRADUATE_SPECIALIZATION' && (
                        <Typography.Text>Especialização</Typography.Text>
                      )}
                      {education.type_education_level === 'MASTERS_DEGREE' && (
                        <Typography.Text>Mestrado</Typography.Text>
                      )}
                      {education.type_education_level === 'PHD_DEGREE' && (
                        <Typography.Text>Doutorado</Typography.Text>
                      )}
                      {education.conclusion_year && education.type_situation === 'FINALIZED' && (
                        <Typography.Text>
                          {' '}
                          - concluído em {education.conclusion_year}
                        </Typography.Text>
                      )}
                      {education.conclusion_year && education.type_situation === 'IN_PROGRESS' && (
                        <Typography.Text>
                          {' '}
                          - previsão de conclusão em {education.conclusion_year}
                        </Typography.Text>
                      )}
                      {!education.conclusion_year && education.start_date && education.end_date && (
                        <Typography.Text>
                          {moment(education.start_date).format('YYYY')} -{' '}
                          {moment(education.end_date).format('YYYY')}
                        </Typography.Text>
                      )}
                    </div>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </Collapse.Panel>
          )}
          {candidate.data?.languages?.length && (
            <Collapse.Panel
              header={<Typography.Title level={3}>Idiomas</Typography.Title>}
              key="languages"
            >
              <Space size="large">
                {candidate.data?.languages?.map((language) => (
                  <Typography.Text key={language.id}>
                    <Typography.Text strong>{language.language}</Typography.Text>
                    {language.proficiency === 'ADVANCED' && ` (avançado)`}
                    {language.proficiency === 'BASIC' && ` (básico)`}
                    {language.proficiency === 'FLUENT' && ` (fluente)`}
                    {language.proficiency === 'INTERMEDIATE' && ` (intermediário)`}
                  </Typography.Text>
                ))}
              </Space>
            </Collapse.Panel>
          )}
          {candidate.data?.knowledge?.length && (
            <Collapse.Panel
              header={<Typography.Title level={3}>Conhecimentos</Typography.Title>}
              key="knowledge"
            >
              {candidate.data?.knowledge?.map((knowledge) => (
                <Tag key={knowledge.id}>{knowledge.description}</Tag>
              ))}
            </Collapse.Panel>
          )}
          {candidate.data?.abilities?.length && (
            <Collapse.Panel
              header={<Typography.Title level={3}>Habilidades</Typography.Title>}
              key="abilities"
            >
              {candidate.data?.abilities?.map((ability) => (
                <Tag key={ability.id}>{ability.description}</Tag>
              ))}
            </Collapse.Panel>
          )}
          {candidate.data?.personal_interests?.length && (
            <Collapse.Panel
              header={<Typography.Title level={3}>Interesses Pessoais</Typography.Title>}
              key="personalInterests"
            >
              {candidate.data?.personal_interests?.map((personalInterest) => (
                <Tag key={personalInterest.id}>{personalInterest.interest}</Tag>
              ))}
            </Collapse.Panel>
          )}
          {(candidate.data?.realocation_possibility !== undefined ||
            candidate.data?.travel_possibility !== undefined ||
            candidate.data?.address_street ||
            candidate.data?.address_number ||
            candidate.data?.address_administrative_region ||
            candidate.data?.address_city ||
            candidate.data?.address_state ||
            candidate.data?.address_country ||
            candidate.data?.address_postal_code) && (
            <Collapse.Panel
              header={<Typography.Title level={3}>Endereço</Typography.Title>}
              key="address"
            >
              <Descriptions
                layout="vertical"
                size="small"
                colon={false}
                labelStyle={{ fontWeight: 'bold' }}
              >
                <Descriptions.Item label="Rua, n°, Bairro">
                  {[
                    candidate.data?.address_street,
                    candidate.data?.address_number,
                    candidate.data?.address_administrative_region,
                  ]
                    .filter((item) => !!item)
                    .join(', ') || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Cidade/Estado">
                  {candidate.data?.address_city || '-'}/{candidate.data?.address_state || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="País">
                  {candidate.data?.address_country || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="CEP">
                  {candidate.data?.address_postal_code || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Possibilidade de mudança">
                  {candidate.data?.realocation_possibility !== undefined &&
                    (candidate.data.realocation_possibility ? (
                      <Typography.Text>Sim</Typography.Text>
                    ) : (
                      <Typography.Text>Não</Typography.Text>
                    ))}
                  {candidate.data?.realocation_possibility === undefined && (
                    <Typography.Text>-</Typography.Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Aceita viajar pela empresa">
                  {candidate.data?.travel_possibility !== undefined &&
                    (candidate.data.travel_possibility ? (
                      <Typography.Text>Sim</Typography.Text>
                    ) : (
                      <Typography.Text>Não</Typography.Text>
                    ))}
                  {candidate.data?.travel_possibility === undefined && (
                    <Typography.Text>-</Typography.Text>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Collapse.Panel>
          )}
          {(candidate.data?.gender !== 'OTHER' ||
            candidate.data?.marital_status ||
            candidate.data?.cpf ||
            candidate.data?.rg ||
            candidate.data?.birthdate ||
            candidate.data?.disabilities?.length) && (
            <Collapse.Panel
              header={<Typography.Title level={3}>Detalhes</Typography.Title>}
              key="details"
            >
              <Descriptions
                layout="vertical"
                size="small"
                colon={false}
                labelStyle={{ fontWeight: 'bold' }}
              >
                {candidate.data?.marital_status && (
                  <Descriptions.Item label="Estado civil">
                    {candidate.data.marital_status === 'MARRIED' && (
                      <Typography.Text>Casado</Typography.Text>
                    )}
                    {candidate.data.marital_status === 'SEPARATED' && (
                      <Typography.Text>Separado</Typography.Text>
                    )}
                    {candidate.data.marital_status === 'DIVORCED' && (
                      <Typography.Text>Divorciado</Typography.Text>
                    )}
                    {candidate.data.marital_status === 'SINGLE' && (
                      <Typography.Text>Solteiro</Typography.Text>
                    )}
                    {candidate.data.marital_status === 'STABLE_UNION' && (
                      <Typography.Text>União estável</Typography.Text>
                    )}
                    {candidate.data.marital_status === 'WIDOW' && (
                      <Typography.Text>Viúvo</Typography.Text>
                    )}
                    {candidate.data.marital_status === 'CONCUBINAGE' && (
                      <Typography.Text>Concubinato</Typography.Text>
                    )}
                    {candidate.data.marital_status === 'OTHER' && (
                      <Typography.Text>Outro</Typography.Text>
                    )}
                  </Descriptions.Item>
                )}
                {candidate.data?.gender !== 'OTHER' && (
                  <Descriptions.Item label="Gênero">
                    {candidate.data?.gender === 'MALE' && (
                      <Typography.Text>Masculino</Typography.Text>
                    )}
                    {candidate.data?.gender === 'FEMALE' && (
                      <Typography.Text>Feminino</Typography.Text>
                    )}
                  </Descriptions.Item>
                )}
                {candidate.data?.cpf && (
                  <Descriptions.Item label="CPF">
                    <NumberFormat
                      value={candidate.data?.cpf}
                      format="###.###.###-##"
                      readOnly
                      displayType="text"
                    />
                  </Descriptions.Item>
                )}
                {candidate.data?.rg && (
                  <Descriptions.Item label="RG">{candidate.data?.rg}</Descriptions.Item>
                )}
                {candidate.data?.disabilities?.length && (
                  <Descriptions.Item label="PCD">
                    <Typography.Text>
                      {candidateDisabilities.data?.results
                        .map((disability) => {
                          switch (disability.type) {
                            case 'HEARING':
                              return 'Auditivo';
                            case 'INTELLECTUAL':
                              return 'Intelectual';
                            case 'MENTAL':
                              return 'Mental';
                            case 'OTHER':
                              return 'Outra';
                            case 'PHYSICAL':
                              return 'Físico';
                            case 'VISUAL':
                              return 'Visual';
                          }
                        })
                        .join(', ')}
                    </Typography.Text>
                  </Descriptions.Item>
                )}
                {candidateDisabilities.data?.results?.length && (
                  <Descriptions.Item label="CID">
                    <Typography.Text>
                      {candidateDisabilities.data?.results
                        .map((disability) => disability.cid || '-')
                        .join(', ')}
                    </Typography.Text>
                  </Descriptions.Item>
                )}
                {candidate.data?.birthdate && (
                  <Descriptions.Item label="Data de Nascimento">
                    {`${moment(candidate.data?.birthdate).format('DD/MM/YYYY')} (${calculateAge(
                      candidate.data?.birthdate
                    )} anos)` || '-'}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Collapse.Panel>
          )}
        </Collapse>
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(ApplicantDetails);
